/**
 * Zielführung: eigene Position auf die Route legen, das nächste Manöver
 * bestimmen, ansagen - und merken, wenn man abgekommen ist.
 *
 * Bewusst ohne Bibliothek. Die Rechnung ist überschaubar: eine Route ist eine
 * Punktfolge, die Position wird auf das nächstgelegene Teilstück projiziert,
 * daraus ergibt sich die zurückgelegte Strecke. Alles Weitere - welches
 * Manöver, welches Tempolimit - hängt an dieser einen Zahl.
 */

const ERDRADIUS = 6371008.8;

const rad = (d) => (d * Math.PI) / 180;

/** Abstand in Metern. Für die Längen hier genügt die Kugel. */
export function distanz(a, b) {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const lat = rad((a.lat + b.lat) / 2);
  const x = dLng * Math.cos(lat);
  return Math.hypot(x, dLat) * ERDRADIUS;
}

/**
 * Position auf die Linie legen. Gibt den Abstand zur Route, die zurückgelegte
 * Strecke und den Punktindex zurück.
 */
function projizieren(punkt, linie, kumuliert, abIndex = 0) {
  let best = { abstand: Infinity, entlang: 0, index: 0 };
  // Ein Stück rückwärts mitsuchen, damit ein GPS-Sprung nicht hängen bleibt.
  const start = Math.max(0, abIndex - 8);
  for (let i = start; i < linie.length - 1; i++) {
    const [ax, ay] = linie[i];
    const [bx, by] = linie[i + 1];
    const cosLat = Math.cos(rad(punkt.lat));
    const px = (punkt.lng - ax) * cosLat;
    const py = punkt.lat - ay;
    const vx = (bx - ax) * cosLat;
    const vy = by - ay;
    const len2 = vx * vx + vy * vy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, (px * vx + py * vy) / len2)) : 0;
    const dx = px - vx * t;
    const dy = py - vy * t;
    const abstand = Math.hypot(dx, dy) * ERDRADIUS * (Math.PI / 180);
    if (abstand < best.abstand) {
      const teil = kumuliert[i + 1] - kumuliert[i];
      best = { abstand, entlang: kumuliert[i] + teil * t, index: i };
    }
  }
  return best;
}

/** Entfernung menschlich: 850 m, 1,2 km, 60 m */
export function strecke(m) {
  if (m < 1000) return `${Math.round(m / 10) * 10} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
}

export function dauer(sekunden) {
  const min = Math.round(sekunden / 60);
  if (min < 60) return `${min} Min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
}

/**
 * Ab welcher Entfernung angesagt wird. Je schneller, desto früher - bei 100
 * km/h sind 400 Meter vier Sekunden Vorlauf, das ist knapp.
 */
function ansageSchwellen(tempoMs) {
  const v = Math.max(8, tempoMs || 8);
  return [
    { stufe: 'voraus', meter: Math.min(1500, Math.max(250, v * 25)) },
    { stufe: 'jetzt', meter: Math.min(200, Math.max(40, v * 4)) }
  ];
}

export function createNavigation({ onUpdate, onAnnounce, onOffRoute }) {
  let route = null;          // { coordinates, maneuvers, limits }
  let kumuliert = [];
  let gesamt = 0;
  let watchId = null;
  let letzterIndex = 0;
  let aktuellesManoever = 0;
  let angesagt = new Set();   // "3:voraus"
  let abseitsSeit = 0;
  let wakeLock = null;

  function vorbereiten(r) {
    route = r;
    kumuliert = [0];
    gesamt = 0;
    for (let i = 1; i < r.coordinates.length; i++) {
      const [ax, ay] = r.coordinates[i - 1];
      const [bx, by] = r.coordinates[i];
      gesamt += distanz({ lng: ax, lat: ay }, { lng: bx, lat: by });
      kumuliert.push(gesamt);
    }
    letzterIndex = 0;
    aktuellesManoever = 0;
    angesagt = new Set();
    abseitsSeit = 0;
  }

  /** Tempolimit am aktuellen Punkt. */
  function limitBei(index) {
    if (!route?.limits?.length) return null;
    for (const l of route.limits) {
      if (index >= l.von && index < l.bis) return l.limit;
    }
    return null;
  }

  function verarbeiten(pos) {
    if (!route) return;
    const hier = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    const p = projizieren(hier, route.coordinates, kumuliert, letzterIndex);
    letzterIndex = p.index;

    // Abseits der Route? Erst nach mehreren Messungen reagieren - ein
    // einzelner ungenauer Punkt ist kein Grund, neu zu rechnen.
    const toleranz = Math.max(35, (pos.coords.accuracy || 20) * 1.5);
    if (p.abstand > toleranz) {
      abseitsSeit += 1;
      if (abseitsSeit === 4) onOffRoute?.(hier);
    } else {
      abseitsSeit = 0;
    }

    // Nächstes Manöver: das erste, dessen Ende noch vor uns liegt.
    while (
      aktuellesManoever < route.maneuvers.length - 1 &&
      kumuliert[route.maneuvers[aktuellesManoever].bis] <= p.entlang + 5
    ) {
      aktuellesManoever += 1;
    }
    const m = route.maneuvers[aktuellesManoever];
    const bisManoever = Math.max(0, kumuliert[m.bis] - p.entlang);
    const uebrig = Math.max(0, gesamt - p.entlang);

    // Ansagen
    const tempo = pos.coords.speed;
    for (const { stufe, meter } of ansageSchwellen(tempo)) {
      const schluessel = `${aktuellesManoever}:${stufe}`;
      if (bisManoever <= meter && !angesagt.has(schluessel)) {
        angesagt.add(schluessel);
        const text = stufe === 'voraus'
          ? `In ${strecke(bisManoever)}: ${m.ansageVoraus}`
          : m.ansageJetzt;
        onAnnounce?.(text);
        break;
      }
    }

    const restAnteil = gesamt > 0 ? uebrig / gesamt : 0;
    onUpdate?.({
      position: hier,
      entlang: p.entlang,
      kurs: Number.isFinite(pos.coords.heading) ? pos.coords.heading : null,
      genauigkeit: pos.coords.accuracy,
      tempoKmh: Number.isFinite(tempo) && tempo >= 0 ? tempo * 3.6 : null,
      limit: limitBei(p.index),
      abstandZurRoute: p.abstand,
      manoever: m,
      bisManoever,
      uebrig,
      restSekunden: restAnteil * route.maneuvers.reduce((a, x) => a + x.sekunden, 0),
      amZiel: uebrig < 25
    });
  }

  async function bildschirmWach(an) {
    try {
      if (an && 'wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen');
      else { await wakeLock?.release(); wakeLock = null; }
    } catch { /* ohne geht es auch, nur schaltet sich der Bildschirm ab */ }
  }

  function start(r) {
    vorbereiten(r);
    bildschirmWach(true);
    watchId = navigator.geolocation.watchPosition(
      verarbeiten,
      () => { /* ein Ausfall ist kein Abbruch - der nächste Punkt kommt */ },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
  }

  function stop() {
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    watchId = null;
    route = null;
    bildschirmWach(false);
  }

  /** Nach einer Neuberechnung weiterlaufen, ohne den Beobachter neu zu setzen. */
  function ersetzen(r) {
    vorbereiten(r);
  }

  /**
   * Wo liegt ein Punkt auf der Route? Für Dinge am Wegrand: eine Tankstelle
   * interessiert nicht nach Luftlinie, sondern danach, wann man an ihr
   * vorbeikommt.
   */
  function entlangWert(punkt) {
    if (!route) return null;
    const p = projizieren(punkt, route.coordinates, kumuliert, 0);
    return { entlang: p.entlang, abstand: p.abstand };
  }

  return { start, stop, ersetzen, entlangWert, laeuft: () => watchId !== null };
}
